import { RangeMode } from '@/types/filters';

/**
 * Value format for `rangeMode` filters:
 * - `"mode:amount"` — inclusive comparison (>= / <=), the default; also what
 *   every value persisted before the including/not-including toggle means.
 * - `"mode:amount:strict"` — strict comparison (> / <). Only More/Less can be
 *   strict; Equal is always inclusive by definition.
 */
export const RANGE_MODE_STRICT_SUFFIX = 'strict';

export interface RangeModeFilterValue {
  mode: RangeMode;
  /** Raw amount string as typed (mappers convert to numbers/minutes themselves) */
  amount: string;
  /** false only for the explicit `:strict` suffix on More/Less */
  isInclusive: boolean;
}

/** Parses a serialized value; null for empty/malformed input. */
export function parseRangeModeFilterValue(value: string | undefined): RangeModeFilterValue | null {
  if (!value || !value.includes(':')) return null;

  const [mode, amount, suffix] = value.split(':');
  if (!amount) return null;

  const isStrict = suffix === RANGE_MODE_STRICT_SUFFIX && mode !== RangeMode.Equal;
  return { mode: mode as RangeMode, amount, isInclusive: !isStrict };
}

/** Serializes to `"mode:amount"` / `"mode:amount:strict"`. Equal is always inclusive. */
export function serializeRangeModeFilterValue({
  mode,
  amount,
  isInclusive,
}: RangeModeFilterValue): string {
  const isStrict = !isInclusive && mode !== RangeMode.Equal;
  return isStrict ? `${mode}:${amount}:${RANGE_MODE_STRICT_SUFFIX}` : `${mode}:${amount}`;
}
